import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'contract',  label: '📄 Contrato' },
  { value: 'proposal',  label: '📑 Proposta' },
  { value: 'invoice',   label: '🧾 Nota Fiscal' },
  { value: 'accounting',label: '📊 Contábil' },
  { value: 'legal',     label: '⚖️ Documentos Legais' },
  { value: 'other',     label: '📁 Outros' },
];

function fileIcon(mimetype, name) {
  if (!mimetype) mimetype = '';
  if (!name) name = '';
  const ext = name.split('.').pop()?.toLowerCase();
  if (mimetype.includes('pdf') || ext === 'pdf') return { icon: '📄', color: 'text-red-600', bg: 'bg-red-50 border-red-200' };
  if (mimetype.includes('excel') || mimetype.includes('spreadsheet') || ext === 'xlsx' || ext === 'xls' || ext === 'csv')
    return { icon: '📊', color: 'text-green-700', bg: 'bg-green-50 border-green-200' };
  if (mimetype.includes('word') || ext === 'doc' || ext === 'docx')
    return { icon: '📝', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' };
  if (mimetype.includes('image') || ['png','jpg','jpeg','gif','webp'].includes(ext))
    return { icon: '🖼️', color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200' };
  if (mimetype.includes('rfc822') || ext === 'eml' || ext === 'msg')
    return { icon: '📧', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' };
  return { icon: '📎', color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200' };
}

function fmtFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsPanel({ api, entityType = 'company', entityId, title }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uploadCategory, setUploadCategory] = useState('other');
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);

  const isLead = entityType === 'lead';
  const baseUrl = isLead ? `/leads/${entityId}/documents` : `/companies/${entityId}/documents`;
  const docBase = isLead ? '/leads/documents' : '/companies/documents';
  const panelTitle = title || (isLead ? 'Documentos do Lead' : 'Documentos da Empresa');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = categoryFilter ? `?category=${categoryFilter}` : '';
      const r = await api.get(`${baseUrl}${params}`);
      setDocs(r.data || []);
    } catch {
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  }, [api, baseUrl, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const uploadFiles = async (files, category) => {
    if (!files?.length) return;
    setUploading(true);
    let success = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', category);
        await api.post(baseUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        success++;
      } catch {
        toast.error(`Erro ao enviar "${file.name}"`);
      }
    }
    setUploading(false);
    if (success > 0) { toast.success(`${success} arquivo(s) enviado(s)`); load(); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    uploadFiles(e.dataTransfer.files, uploadCategory);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Remover este documento?')) return;
    try {
      await api.delete(`${docBase}/${docId}`);
      toast.success('Documento removido');
      load();
    } catch {
      toast.error('Erro ao remover documento');
    }
  };

  const getDownloadUrl = (docId) => {
    const base = api.defaults?.baseURL || '';
    return `${base}${docBase}/${docId}/download`;
  };

  const handlePreview = (doc) => {
    if (doc.mimetype?.includes('pdf')) setPreviewDoc(doc);
    else window.open(getDownloadUrl(doc.id), '_blank');
  };

  const categoryLabel = (v) => CATEGORIES.find(c => c.value === v)?.label?.replace(/^[^\s]+\s/, '') || v || 'Outros';

  return (
    <div className="space-y-4">
      {/* Header + upload */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">📁</span>
          <h3 className="font-semibold text-gray-800">{panelTitle}</h3>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{docs.length}</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={uploadCategory} onValueChange={setUploadCategory}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="text-xs bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white px-3 py-1.5 rounded-lg font-medium transition flex items-center gap-1"
          >
            {uploading ? <><span className="animate-spin">⏳</span> Enviando…</> : <><span>⬆️</span> Upload</>}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.eml,.msg,.txt,.png,.jpg,.jpeg"
            onChange={e => { uploadFiles(e.target.files, uploadCategory); e.target.value = ''; }}
          />
        </div>
      </div>

      {/* Category filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500">Filtrar por categoria:</span>
        <Select value={categoryFilter || '__all__'} onValueChange={v => setCategoryFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Todas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas as categorias</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Drop zone / list */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !docs.length && !loading && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl transition-colors ${dragging ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-gray-50/50'} ${!docs.length && !loading ? 'cursor-pointer' : ''}`}
      >
        {loading ? (
          <div className="py-8 text-center text-gray-400 text-sm">Carregando…</div>
        ) : docs.length === 0 ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-2">📂</div>
            <p className="text-sm font-medium text-gray-600">Arraste arquivos aqui ou clique para selecionar</p>
            <p className="text-xs text-gray-400 mt-1">PDF, Excel, Word, Imagens, Emails (.eml) — máx. 25 MB</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {docs.map(doc => {
              const fi = fileIcon(doc.mimetype, doc.original_name);
              return (
                <div key={doc.id} className="flex items-center gap-3 px-4 py-3 hover:bg-white transition-colors group">
                  <div className={`w-9 h-9 rounded-lg border flex items-center justify-center text-lg flex-shrink-0 ${fi.bg}`}>
                    {fi.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.original_name}</p>
                    <p className="text-xs text-gray-400">
                      {fmtFileSize(doc.size)} · {new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}
                      {doc.uploaded_by_name && ` · ${doc.uploaded_by_name}`}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200 shrink-0">
                    {categoryLabel(doc.category)}
                  </Badge>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button onClick={() => handlePreview(doc)} title="Visualizar / Abrir"
                      className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded transition">👁️</button>
                    <a href={getDownloadUrl(doc.id)} download={doc.original_name} title="Baixar"
                      className="text-xs bg-green-100 hover:bg-green-200 text-green-700 px-2 py-1 rounded transition">⬇️</a>
                    <button onClick={() => handleDelete(doc.id)} title="Remover"
                      className="text-xs bg-red-100 hover:bg-red-200 text-red-600 px-2 py-1 rounded transition">🗑️</button>
                  </div>
                </div>
              );
            })}
            <div className={`px-4 py-2 text-center text-xs text-gray-400 ${dragging ? 'bg-indigo-50 text-indigo-500' : ''}`}>
              {dragging ? '↓ Solte para adicionar' : 'Arraste mais arquivos aqui para adicionar'}
            </div>
          </div>
        )}
      </div>

      {/* PDF Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setPreviewDoc(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden" style={{ height: '85vh' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
              <div className="flex items-center gap-2">
                <span>📄</span>
                <span className="font-medium text-sm text-gray-800 truncate max-w-xs">{previewDoc.original_name}</span>
                <span className="text-xs text-gray-400">{fmtFileSize(previewDoc.size)}</span>
              </div>
              <div className="flex gap-2">
                <a href={getDownloadUrl(previewDoc.id)} download={previewDoc.original_name}
                  className="text-xs bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1.5 rounded-lg transition font-medium">⬇️ Baixar</a>
                <button onClick={() => setPreviewDoc(null)}
                  className="text-gray-400 hover:text-gray-700 text-xl font-bold px-2">✕</button>
              </div>
            </div>
            <iframe
              src={`${getDownloadUrl(previewDoc.id)}#toolbar=1&navpanes=1`}
              className="w-full"
              style={{ height: 'calc(100% - 56px)' }}
              title={previewDoc.original_name}
            />
          </div>
        </div>
      )}
    </div>
  );
}
